import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Building2,
  Zap,
  Lock,
  ArrowRight,
  CheckCircle2,
  CreditCard,
  HelpCircle,
} from "lucide-react";
import usePageMeta from "../lib/usePageMeta";

/**
 * /help/pay-by-bank
 *
 * Long-form trust page for first-time UK Open Banking customers. Linked from
 * the checkout "How Pay by Bank works" expander. Keeps the conversion-killing
 * questions ("is this safe?", "what is Open Banking?", "how is it different
 * from a card?") off the checkout page itself, so checkout stays focused.
 */
export default function HelpPayByBank() {
  usePageMeta(
    "Pay by Bank — Is it safe? How it works | Peptigen Labs",
    "Everything you need to know about paying for your Peptigen Labs research order by UK Open Banking — FCA-regulated, bank-grade security, instant Faster Payments. Trusted by millions of UK customers.",
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="pay-by-bank-help-page">
      <div className="pg-micro mb-3">// Help · Payments</div>
      <h1
        className="text-[#F7FAFC] text-4xl sm:text-5xl pg-display mb-4"
        style={{ fontFamily: "'Exo 2'" }}
      >
        Paying by Bank — explained.
      </h1>
      <p className="text-[#9CA8B8] text-base leading-relaxed mb-10 max-w-2xl">
        Open Banking is the UK&apos;s officially-regulated way to pay directly from
        your current account — without ever sharing card details. Faster than
        cards, more secure than cards, and used by millions of people every day.
        Here&apos;s exactly how it works for your Peptigen Labs order.
      </p>

      {/* Trust strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-12">
        <BigTrust
          Icon={Building2}
          title="FCA-regulated"
          body="Our payment partner Fena is authorised and regulated by the UK Financial Conduct Authority (firm reference 928060). They are subject to the same supervision as your bank."
        />
        <BigTrust
          Icon={ShieldCheck}
          title="Bank-grade security"
          body="You authorise every payment inside your own banking app, with your own biometrics. Peptigen Labs and Fena never see, store or have access to your bank login."
        />
        <BigTrust
          Icon={Zap}
          title="Instant Faster Payments"
          body="Transfers settle in seconds via the UK Faster Payments network, the same system your salary lands through. Fully traceable. Refundable through your bank."
        />
      </div>

      {/* The 4 steps */}
      <Section title="How a Pay by Bank checkout works">
        <ol className="space-y-5">
          <BigStep n={1} title="Click 'Pay by Bank' at checkout">
            On the Peptigen Labs checkout page you&apos;ll see a single
            &quot;Pay by Bank&quot; button. Click it once your shipping details
            and the research-use-only acknowledgement are complete.
          </BigStep>
          <BigStep n={2} title="Pick your bank from the list">
            A secure pop-up shows every UK current-account bank — Barclays,
            HSBC, Lloyds, NatWest, Santander, Halifax, Nationwide, Monzo,
            Starling, Revolut, First Direct, TSB and more. Choose yours.
          </BigStep>
          <BigStep n={3} title="Authenticate inside your own banking app">
            You&apos;re sent straight into your bank&apos;s own secure environment
            — usually their app on your phone. You log in with Face ID,
            fingerprint or your normal passcode. <strong className="text-[#F7FAFC]">
            Peptigen Labs and Fena never see this step.</strong>
          </BigStep>
          <BigStep n={4} title="Confirm the payment, you're done">
            Your bank shows you the exact amount and the recipient
            (&quot;Peptigen Labs Ltd&quot;). You tap to confirm. The Faster
            Payment lands instantly, your order is confirmed, and your bank
            sends you a receipt as it would for any other transfer.
          </BigStep>
        </ol>
      </Section>

      {/* Comparison table */}
      <Section title="Pay by Bank vs paying by card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-[#1E2E44]" data-testid="comparison-table">
            <thead>
              <tr className="bg-[#0B1220]">
                <th className="text-left p-4 pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8] font-normal">
                  &nbsp;
                </th>
                <th className="text-left p-4 pg-mono text-[10px] uppercase tracking-[0.18em] text-[#A6FF00] font-normal">
                  Pay by Bank
                </th>
                <th className="text-left p-4 pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8] font-normal">
                  Card payment
                </th>
              </tr>
            </thead>
            <tbody>
              <CompRow label="Card details required" pbb="Never" card="Yes — full long card number" pbbWin />
              <CompRow label="Authentication" pbb="Inside your own banking app" card="3D Secure pop-up + SMS code" pbbWin />
              <CompRow label="Settlement speed" pbb="Instant (Faster Payments)" card="2–3 business days" pbbWin />
              <CompRow label="Risk of card-data breach" pbb="None — no card data exists" card="Possible if merchant is breached" pbbWin />
              <CompRow label="Refundable" pbb="Yes, by your bank" card="Yes, by the card scheme" tie />
              <CompRow label="UK regulator" pbb="FCA + UK Open Banking" card="FCA + Visa/Mastercard rules" tie />
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-[#9CA8B8] mt-3 leading-relaxed">
          Open Banking is regulated under the UK&apos;s Payment Services Regulations 2017
          and supervised by the Financial Conduct Authority. It is the same legal
          framework that protects bank-to-bank transfers you already make every day.
        </p>
      </Section>

      {/* FAQ */}
      <Section title="Frequently asked questions">
        <div className="space-y-4">
          <Faq q="Is Pay by Bank safe?">
            Yes — it&apos;s arguably safer than paying by card. You never share any
            card number, CVV, or expiry date with us. The payment is authorised
            inside your bank&apos;s own app, secured by your bank&apos;s own
            biometrics. Even if Peptigen Labs or our payment partner were
            somehow breached, there is no card data to steal.
          </Faq>
          <Faq q="Who is Fena?">
            <Link to="https://fena.co" target="_blank" rel="noreferrer" className="text-[#5EEAD4] hover:text-[#A6FF00]">Fena</Link>{" "}
            is the FCA-authorised UK Open Banking provider that powers our
            checkout (firm reference 928060). They are licensed under the same
            regulatory regime as the high-street banks. Fena handles the secure
            handoff between Peptigen Labs and your bank — they never store your
            login credentials and never have access to your wider account.
          </Faq>
          <Faq q="Will I get a receipt? What appears on my bank statement?">
            Yes. Your bank issues you a Faster Payments receipt the moment the
            transfer completes — exactly like any other transfer you make. On
            your statement the entry will read &quot;PEPTIGEN LABS LTD&quot;
            with your unique order reference.
          </Faq>
          <Faq q="Can I get a refund?">
            Absolutely. Refunds are processed back to the same bank account
            you paid from, usually within 1–2 working days. You&apos;re also
            covered by your bank&apos;s standard payment-protection rules under
            the UK Payment Services Regulations. See our{" "}
            <Link to="/legal/refunds" className="text-[#5EEAD4] hover:text-[#A6FF00]">
              Refund Policy
            </Link>{" "}
            for full details.
          </Faq>
          <Faq q="What if I don't have mobile banking set up?">
            Most UK banks also support Pay by Bank through their web banking,
            so you can complete the payment from a desktop browser. If your
            bank only supports mobile authentication, you can scan a QR code
            on the checkout page from your phone to continue.
          </Faq>
          <Faq q="Why don't you accept cards?">
            We&apos;re a small UK operation and Pay by Bank gives us — and
            therefore you — a faster, more secure, lower-cost route to checkout.
            Card networks charge 1.5–3% on every order, which would mean higher
            prices. Pay by Bank also eliminates card-fraud and chargeback risk,
            which means we can keep margins tight and reinvest in product
            quality and same-day UK dispatch.
          </Faq>
          <Faq q="My bank isn't on the list — can I still pay?">
            Every major UK current-account bank supports Open Banking by law.
            If your bank doesn&apos;t appear in the picker, contact us at{" "}
            <a href="mailto:info@peptigenlabs.co.uk" className="text-[#5EEAD4] hover:text-[#A6FF00]">
              info@peptigenlabs.co.uk
            </a>{" "}
            and we&apos;ll arrange a manual bank-transfer invoice.
          </Faq>
          <Faq q="What does Peptigen Labs see during the payment?">
            We see only that the payment was successful, the amount, and your
            order reference. We never receive your bank login, your account
            number, your sort code, your card details, or any view of your
            wider account activity. Open Banking gives us a one-time
            confirmation, nothing more.
          </Faq>
        </div>
      </Section>

      {/* CTA back to checkout */}
      <div className="mt-12 p-6 sm:p-8 border border-[#A6FF00]/40 bg-[#A6FF00]/[0.04] rounded-sm">
        <div className="flex flex-col sm:flex-row gap-5 sm:items-center sm:justify-between">
          <div>
            <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4] mb-2">
              Ready to checkout?
            </div>
            <h3 className="text-[#F7FAFC] text-2xl pg-heading mb-1">
              Pick up where you left off.
            </h3>
            <p className="text-[#9CA8B8] text-sm">
              Your basket is still saved. Order before 14:00 GMT for next-working-day UK delivery.
            </p>
          </div>
          <Link
            to="/checkout"
            className="pg-btn-primary shrink-0"
            data-testid="back-to-checkout-cta"
          >
            <Lock size={16} /> Back to Checkout <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-14">
      <h2
        className="text-[#F7FAFC] text-2xl sm:text-3xl pg-heading mb-6"
        style={{ fontFamily: "'Rajdhani'" }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function BigTrust({ Icon, title, body }) {
  return (
    <div className="px-5 py-5 rounded-sm border border-[#1E2E44] bg-[#0B1220]/60 hover:border-[#5EEAD4]/40 transition-colors">
      <Icon size={20} className="text-[#5EEAD4] mb-3" />
      <div className="text-[#F7FAFC] pg-heading text-base mb-1.5" style={{ fontFamily: "'Rajdhani'" }}>
        {title}
      </div>
      <div className="text-[#9CA8B8] text-[13px] leading-relaxed">{body}</div>
    </div>
  );
}

function BigStep({ n, title, children }) {
  return (
    <li className="flex gap-4 items-start">
      <div
        className="shrink-0 w-9 h-9 rounded-full bg-[#0B1220] border border-[#5EEAD4]/50
                   flex items-center justify-center pg-mono text-[13px] text-[#5EEAD4]"
      >
        {n}
      </div>
      <div className="pt-1">
        <div className="text-[#F7FAFC] text-base font-semibold mb-1">{title}</div>
        <div className="text-[#D5DCE8] text-[14px] leading-relaxed">{children}</div>
      </div>
    </li>
  );
}

function CompRow({ label, pbb, card, pbbWin, tie }) {
  return (
    <tr className="border-t border-[#1E2E44]">
      <td className="p-4 text-[#9CA8B8] text-[13px]">{label}</td>
      <td className="p-4">
        <span className="inline-flex items-center gap-2 text-[#F7FAFC] text-[13px]">
          {(pbbWin || tie) && <CheckCircle2 size={14} className="text-[#A6FF00]" />}
          {pbb}
        </span>
      </td>
      <td className="p-4">
        <span className="inline-flex items-center gap-2 text-[#9CA8B8] text-[13px]">
          {tie && <CheckCircle2 size={14} className="text-[#5EEAD4]/60" />}
          {!tie && pbbWin && <CreditCard size={14} className="text-[#9CA8B8]/60" />}
          {card}
        </span>
      </td>
    </tr>
  );
}

function Faq({ q, children }) {
  return (
    <details className="group border border-[#1E2E44] rounded-sm bg-[#0B1220]/60 hover:border-[#00C2D6]/40 transition-colors">
      <summary className="cursor-pointer list-none px-5 py-4 flex items-start gap-3">
        <HelpCircle size={16} className="text-[#5EEAD4] mt-0.5 shrink-0" />
        <span className="text-[#F7FAFC] text-[15px] font-semibold flex-1">{q}</span>
        <span className="text-[#9CA8B8] text-xs pg-mono group-open:rotate-90 transition-transform">›</span>
      </summary>
      <div className="px-5 pb-5 pl-12 text-[#D5DCE8] text-[14px] leading-relaxed">
        {children}
      </div>
    </details>
  );
}
